import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import MyApiClient from '../../../../../api-client/MyApiClient';
import { FirmaUpload } from '../../../../components/Consiglio/FirmaUpload';
import { ruoloConsiglio } from '../../../../components/Consiglio/ruoli';
import { usePageTitle } from "../../../../layouts/PageHeaderExtra";

const LISTA_CDA = '/struttura/cda/list';

const formatDate = (iso) => {
	if (!iso) return '—';
	const [anno, mese, giorno] = String(iso).substring(0, 10).split('-');
	if (!anno || !mese || !giorno) return iso;
	return `${giorno}/${mese}/${anno}`;
};

const iniziali = (nome) =>
	String(nome || '').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase();

export const FirmaConsiglio = () => {

	const navigate = useNavigate();
	const { id } = useParams();

	const [membro, setMembro] = useState(null);
	// Titolo della pagina = nome del membro del consiglio; finché non è caricato resta quello generico
	usePageTitle(membro?.name || '');
	const [loadingShowed, setLoadingShowed] = useState(false);
	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	const [firmaScelta, setFirmaScelta] = useState(null);
	const [firmaDaRimuovere, setFirmaDaRimuovere] = useState(false);

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	const carica = useCallback(async () => {
		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.getClubBoard(auth.token, auth.clubId);
		setLoadingShowed(false);

		const trovato = Array.isArray(risultato) ? risultato.find(m => m.membershipId === id) : null;
		if (!trovato) {
			mostraErrore("Membro del consiglio direttivo non trovato.");
			return;
		}
		setMembro(trovato);
	}, [id]);

	useEffect(() => {
		carica();
	}, [carica]);

	const salva = async () => {
		if (!firmaScelta && !firmaDaRimuovere) {
			mostraErrore("Scegli un file con la firma, oppure rimuovi quella esistente.");
			return;
		}

		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = firmaScelta
			? await apiClient.updateBoardMemberSignature(auth.token, auth.clubId, id, firmaScelta)
			: await apiClient.deleteBoardMemberSignature(auth.token, auth.clubId, id);
		setLoadingShowed(false);

		if (risultato && risultato.error) {
			mostraErrore("Non è stato possibile salvare la firma: " + risultato.error);
			return;
		}
		navigate(LISTA_CDA);
	};

	const ruolo = membro ? ruoloConsiglio(membro.role) : null;

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<div style={{ width: '50%', minWidth: '440px' }}>
				<div className="card">
					<div className="card-body d-flex align-items-center gap-3">
						<span className="sid-cd-avatar" style={{ width: '48px', height: '48px', fontSize: '16px' }}>
							{membro && membro.picture && membro.picture.uri
								? <img src={membro.picture.uri} alt={membro.name} />
								: iniziali(membro?.name)}
						</span>
						<div>
							<h4 className="card-title mb-1">{membro?.name || '—'}</h4>
							<div className="d-flex align-items-center gap-2 flex-wrap">
								{ruolo && <span className={`sid-pill ${ruolo.pill}`}>{ruolo.label}</span>}
								{membro && (
									<span className="text-muted" style={{ fontSize: '13px' }}>
										Mandato {formatDate(membro.mandateStart)} – {formatDate(membro.mandateEnd)}
									</span>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className="card">
					<div className="card-header">
						<h4 className="card-title">Firma digitale</h4>
					</div>
					<div className="card-body">
						<p className="text-muted" style={{ fontSize: '13px' }}>
							Carica un file grafico con la firma di {membro?.name || 'questo membro'}. Verrà utilizzata
							nei verbali e nei documenti ufficiali del consiglio direttivo.
						</p>
						<FirmaUpload
							firmaCorrente={membro?.signature}
							onSelezione={(firma) => { setFirmaScelta(firma); setFirmaDaRimuovere(false); }}
							onRimuovi={() => { setFirmaScelta(null); setFirmaDaRimuovere(true); }}
							onErrore={mostraErrore}
						/>
					</div>
				</div>

				<div className="d-flex justify-content-end gap-2">
					<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_CDA)}>Annulla</Button>
					<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salva}>Salva firma</Button>
				</div>
			</div>
		</>
	);
};

export default FirmaConsiglio;
